import { computePosition, MATRIX_CHART_SIZE } from 'affiny-core/helpers/graphs';
import {
  getGraphDimension,
  VISION_INTRO_GRAPH_ID,
} from 'affiny-core/helpers/personalityReport';
import {
  EPersonalityTypes,
  IPersonalityReportGraph,
} from 'affiny-core/modules/personalityReport/types';
import { useCheckTrad, useTitlePosition } from 'affiny-desktop/src/hooks';
import {
  Box,
  Flex,
  MatrixGrid,
  rem,
  ShadowBox,
  Text,
  ValueBar,
} from 'affiny-ui/src';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PersonalityReportLayerMatchingVisions } from './PersonalityReportLayerMatchingVisions';
import { PersonalityReportLayerParagraph } from './PersonalityReportLayerParagraph';
import { PersonalityReportLayerTitle } from './PersonalityReportLayerTitle';

interface IPersonalityReportLayerVisionSubChapter {
  graph: IPersonalityReportGraph;
  personalityType: EPersonalityTypes;
  setTitle: (title: string) => void;
}

export const PersonalityReportLayerVisionSubChapter = ({
  graph,
  setTitle,
}: IPersonalityReportLayerVisionSubChapter) => {
  const { t, i18n } = useTranslation();
  const titleRef = useRef<HTMLDivElement>(null);

  useTitlePosition({ setTitle, title: graph.id, titleRef });

  const tradVisionKey = `personalityReport.vision.detailedVision.${graph.id}`;
  const tradGraphKey = `${tradVisionKey}.graphLabels`;
  const matrix = graph.matrices[0];
  const graphDimension = getGraphDimension(graph);

  const onDimLeftTrad = useCheckTrad(
    `${tradGraphKey}.${matrix.left_label_key}`
  );
  const onDimRightTrad = useCheckTrad(
    `${tradGraphKey}.${matrix.right_label_key}`
  );
  const twoDimTopLeftTrad = useCheckTrad(
    `${tradGraphKey}.${matrix.left_top_label_key}`
  );
  const twoDimTopRightTrad = useCheckTrad(
    `${tradGraphKey}.${matrix.right_top_label_key}`
  );
  const twoDimBottomLeftTrad = useCheckTrad(
    `${tradGraphKey}.${matrix.left_bottom_label_key}`
  );
  const twoDimBottomRightTrad = useCheckTrad(
    `${tradGraphKey}.${matrix.right_bottom_label_key}`
  );

  const intros: string[] | false =
    i18n.exists(`${tradVisionKey}.intro`) &&
    Object.values(
      t(`${tradVisionKey}.intro`, { returnObjects: true, defaultValue: {} })
    );

  const graphItems =
    i18n.exists(`${tradVisionKey}.graphDescriptionItem`) &&
    Object.values(
      t(`${tradVisionKey}.graphDescriptionItem`, {
        returnObjects: true,
        defaultValue: {},
      })
    );

  const renderGraphItem = (label: string, index: number) => (
    <ListItem key={index}>{label}</ListItem>
  );

  const oneDimensionGraph = useMemo(
    () => (
      <Box>
        <ShadowBox px="large" py="xLarge" width={rem(255)}>
          <Text mb="large">{t(`${tradVisionKey}.title`)}</Text>
          <ValueBar
            title={onDimLeftTrad}
            rightTitle={onDimRightTrad}
            value={matrix.viewer.x || 0}
            titleSize={10}
          />
        </ShadowBox>
      </Box>
    ),
    [t, matrix, tradVisionKey, onDimLeftTrad, onDimRightTrad]
  );

  const twoDimensionGraph = useMemo(
    () => (
      <ShadowBox
        width={rem(544)}
        margin="auto"
        display="flex"
        flexDirection="column"
        alignItems="center"
        p="large"
        mb="xLarge"
      >
        {i18n.exists(`${tradVisionKey}.graphTitle`) && (
          <Text mb="large">{t(`${tradVisionKey}.graphTitle`)}</Text>
        )}
        <MatrixGrid
          titleTopLeft={twoDimTopLeftTrad}
          titleTopRight={twoDimTopRightTrad}
          titleBottomLeft={twoDimBottomLeftTrad}
          titleBottomRight={twoDimBottomRightTrad}
          valueX={computePosition(MATRIX_CHART_SIZE, matrix.viewer.x)}
          valueY={computePosition(MATRIX_CHART_SIZE, matrix.viewer.y)}
        />
        {graphItems && (
          <Box mt="large">
            <Text>{t(`${tradVisionKey}.graphDescription`)}</Text>
            <List>{graphItems.map(renderGraphItem)}</List>
          </Box>
        )}
      </ShadowBox>
    ),
    [
      t,
      tradVisionKey,
      matrix,
      graphItems,
      i18n,
      twoDimTopLeftTrad,
      twoDimTopRightTrad,
      twoDimBottomLeftTrad,
      twoDimBottomRightTrad,
    ]
  );

  return (
    <Box>
      <div ref={titleRef}>
        <PersonalityReportLayerTitle isPrimary id={graph.id}>
          {t(`${tradVisionKey}.title`)}
        </PersonalityReportLayerTitle>
      </div>

      {intros && (
        <Flex>
          <Box>
            {intros.map((intro, idx) => (
              <PersonalityReportLayerParagraph
                key={`intro${idx}`}
                style={{ mr: 'xLarge' }}
              >
                {intro}
              </PersonalityReportLayerParagraph>
            ))}
          </Box>
          {graphDimension === 1 && oneDimensionGraph}
        </Flex>
      )}

      {graphDimension === 2 && <>{twoDimensionGraph}</>}

      <PersonalityReportLayerTitle>
        {t(`personalityReport.psyAnalysis`)}
      </PersonalityReportLayerTitle>
      {graph.analysis.map((text, idx) => (
        <PersonalityReportLayerParagraph key={`analysis${idx}`}>
          {text}
        </PersonalityReportLayerParagraph>
      ))}

      {graph.id === VISION_INTRO_GRAPH_ID ? (
        <>
          <PersonalityReportLayerTitle>
            {t(`personalityReport.vision.matchingVisions`)}
          </PersonalityReportLayerTitle>
          <PersonalityReportLayerMatchingVisions graph={graph} />
        </>
      ) : null}
    </Box>
  );
};

const List = styled.ul`
  margin: 0;
  padding: 0;
`;

const ListItem = styled.li`
  :before {
    content: '•';
    margin-right: ${({ theme }) => theme.space.large};
  }
  padding: 0;
  list-style: none;
  color: ${({ theme }) => theme.colors.primary};
  line-height: ${({ theme }) => theme.space.large};
  &:last-child {
    margin-bottom: 0;
  }
`;
